import { openDayDate } from '../../utils/openDayDate';
/** @jsxImportSource @emotion/react */
import React from 'react';
import { css } from '@emotion/react';
import { Button, Card, Space, Tag, Tooltip, Typography } from 'antd';
import {
  StepForwardOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../i18n';
import {
  useAdvanceSaveSimDaysMutation,
  useGetSaveRuntimeSessionQuery,
} from '../../api/simulationApi';
import { useRequestHalt } from '../../utils/requestHalt';
import { selectIsAuthenticated } from '../../store/slices/authSlice';

const { Text } = Typography;

const styles = {
  wrap: css({
    position: 'fixed',
    right: 16,
    bottom: 16,
    zIndex: 1200,
    width: 300,
    boxShadow: '0 8px 24px rgba(0,0,0,0.16)',
    borderRadius: 10,
  }),
  toolbarWrap: css({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 10px',
    border: '1px solid #f0f0f0',
    borderRadius: 8,
    background: '#fff',
  }),
  row: css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  }),
};

interface SimCornerControlsProps {
  farmId?: string;
  onAdvanceDone?: () => void;
  mode?: 'corner' | 'toolbar';
}

const formatSimDate = (value?: string | null) => {
  if (!value) return '—';
  const parsed = dayjs(value);
  return parsed.isValid() ? formatDate(parsed.format('YYYY-MM-DD'), 'short') : String(value).slice(0, 10);
};

const SimCornerControls: React.FC<SimCornerControlsProps> = ({ onAdvanceDone, mode = 'corner' }) => {
  const { t } = useTranslation('simulation');
  const navigate = useNavigate();
  const location = useLocation();
  const saveMatch = location.pathname.match(/^\/saves\/([^/]+)/);
  const currentSaveId = saveMatch?.[1] || null;
  const simulationPath = currentSaveId
    ? `/saves/${currentSaveId}/simulation`
    : `/saves`;
  const isSimulationPage = location.pathname === simulationPath;
  const { halted } = useRequestHalt();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const { data: session } = useGetSaveRuntimeSessionQuery(currentSaveId || '', {
    skip: !currentSaveId || halted || !isAuthenticated,
    pollingInterval: 15_000,
  });
  const [advanceDays, { isLoading: advancing }] = useAdvanceSaveSimDaysMutation();

  if (!currentSaveId || !session || (mode === 'toolbar' && isSimulationPage)) return null;

  const isActive = session.status === 'active';

  const runAdvance = async () => {
    await advanceDays({ saveId: currentSaveId, days: 1 }).unwrap();
    onAdvanceDone?.();
  };

  const actionButtons = (
    <Space wrap>
      {isActive && (
        <Tooltip title={t('corner.advanceDay')}>
          <Button icon={<StepForwardOutlined />} onClick={runAdvance} loading={advancing} disabled={advancing} />
        </Tooltip>
      )}
    </Space>
  );

  if (mode === 'toolbar') {
    return (
      <div css={styles.toolbarWrap}>
        <Space size={6}>
          <ThunderboltOutlined />
          <Text strong>{t('corner.title')}</Text>
          <Tag color={isActive ? 'green' : 'orange'}>{session.status}</Tag>
          <Text type="secondary">{formatSimDate(openDayDate(session.simDate, 'free'))}</Text>
        </Space>
        {actionButtons}
        <Button type="link" size="small" onClick={() => navigate(simulationPath)}>
          {t('common:actions.open')}
        </Button>
      </div>
    );
  }

  return (
    <Card
      size="small"
      css={styles.wrap}
      title={(
        <Space>
          <ThunderboltOutlined />
        <span>{t('corner.title')}</span>
        <Tag color={isActive ? 'green' : 'orange'}>{session.status}</Tag>
      </Space>
      )}
      extra={
        <Button type="link" size="small" onClick={() => navigate(simulationPath)}>
          {t('common:actions.open')}
        </Button>
      }
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        <div css={styles.row}>
          <Text type="secondary">{t('common:labels.date')}</Text>
          <Text strong>{formatSimDate(openDayDate(session.simDate, 'free'))}</Text>
        </div>
        {actionButtons}
      </Space>
    </Card>
  );
};

export default SimCornerControls;
